import { useQuery } from '@tanstack/react-query';
import { orpc } from '@/api/rpc/client';
import type { TaskCount } from '@erp/rpc';

/**
 * Query key for task counts - exported for cache invalidation
 */
export const TASK_COUNTS_QUERY_KEY = ['home', 'taskCounts'] as const;

/**
 * Hook to fetch task counts for the home page
 *
 * Features:
 * - Refetches on window focus
 * - Refetches every 30 seconds
 * - Returns counts keyed by task ID for easy lookup
 */
export function useHomeTaskCounts(enabled = true) {
  const query = useQuery({
    ...orpc.home.tasks.getTaskCounts.queryOptions({}),
    queryKey: TASK_COUNTS_QUERY_KEY,
    enabled,
    refetchOnWindowFocus: true,
    refetchInterval: 30000, // 30 seconds
  });

  // Transform counts array into a map for easy lookup
  const countsMap = new Map<string, number>();
  if (query.data?.counts) {
    for (const count of query.data.counts) {
      countsMap.set(count.taskId, count.count);
    }
  }

  return {
    ...query,
    counts: query.data?.counts ?? [],
    countsMap,
    getCount: (taskId: string): number => countsMap.get(taskId) ?? 0,
  } satisfies typeof query & {
    counts: TaskCount[];
    countsMap: Map<string, number>;
    getCount: (taskId: string) => number;
  };
}
